import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { NotificationType } from "components/ScopedNotificationSettings/utils";
import { formatNotificationRolesOptions } from "components/ScopedNotificationSettings/utils";
import type { TButtonMenuOption } from "components/shared/ButtonOptionsMenu";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { Binary } from "types";

import type { RootState } from "./store";
import type { TFetchStatus, TParamsPagination } from "./types";

/**
 * This store manages received notifications and their state
 * It handles:
 * - Fetching notifications history
 * - Managing notification status (seen/unseen)
 * - Accessing notification-related information
 * - etc
 */
export const getNotifications = createAsyncThunk(
    "notification/get",
    async (baseUrl: string, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const { seen, ...params } = state.notification.optionsPaginationNotifications;
        try {
            const response = await axios.get<{
                count: number;
                offset: number;
                entries: INotification[];
            }>(baseUrl + `/notifications`, { params });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAllNotifications = createAsyncThunk(
    "notification/getAll",
    async (baseUrl: string, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        try {
            const response = await axios.get<{
                count: number;
                offset: number;
                entries: INotification[];
            }>(baseUrl + `/notifications`, {
                params: state.notification.optionsPaginationNotifications,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const markNotifAsSeen = createAsyncThunk(
    "notification/put",
    async ({ baseUrl, id }: { baseUrl: string; id: number | number[] }, { rejectWithValue }) => {
        try {
            const response = await axios.put(baseUrl + `/notifications`, { id });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const markAllNotifAsSeen = createAsyncThunk(
    "notification/putAll",
    async ({ baseUrl, id }: { baseUrl: string; id: number | number[] }, { rejectWithValue }) => {
        try {
            const response = await axios.put(baseUrl + `/notifications`, { id });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Get notifications roles preferences
 */
export const getNotificationRoles = createAsyncThunk(
    "notification/getNotificationRoles",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/notifications/roles`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateNotificationRoles = createAsyncThunk(
    "notification/updateNotificationRoles",
    async ({ data }: { data: DTOAssignNotificationPermissions }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/notifications/roles`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface DTOAssignNotificationPermissions {
    [key: string]: RoleValidate | undefined;
}

export type RoleValidate = Record<ROLES | string, boolean | Binary>;
export type NotificationRolePermissions = {
    notificationType: NotificationType;
    shouldNotify: RoleValidate;
};

export interface INotification {
    id: number;
    name: string;
    seen: boolean;
    title: string;
    description: string;
    companyId: number;
    avatarUrl: string;
    created: string;
    actions: INotificationAction[];
}

export interface INotificationAction {
    title: string;
    data:
        | "beneficiary"
        | "term"
        | "profile"
        | "statement"
        | "document"
        | "title"
        | "publication"
        | "external"
        | "none"
        | "agreement"
        | "transferTermSales"
        | "import";
    type: "get" | "approve" | "decline" | "dismiss";
    payload: Record<string, any>;
}

interface NotificationStoreStatus {
    notificationRolesPermissions: TFetchStatus;
    updateNotificationRoles: TFetchStatus;
}

export type NotificationRoleOption = Record<NotificationType, TButtonMenuOption[]>;

interface NotificationState {
    notifications: INotification[];
    allNotifications: INotification[];
    countNotifications: number;
    optionsPaginationNotifications: Partial<TParamsPagination> & {
        seen: "all" | null;
    };
    notificationRolesPermissions: NotificationRolePermissions[] | undefined;
    fetchStatus: NotificationStoreStatus;
    notificationRolesOptions: Record<NotificationType, TButtonMenuOption[]> | undefined;
}

const initialState: NotificationState = {
    notifications: [],
    allNotifications: [],
    countNotifications: 0,
    optionsPaginationNotifications: { limit: 100, seen: "all" },
    notificationRolesPermissions: undefined,
    fetchStatus: {
        notificationRolesPermissions: "idle",
        updateNotificationRoles: "idle",
    },
    notificationRolesOptions: undefined,
};

const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        setOptionsPaginationNotifications: (
            state,
            {
                payload,
            }: {
                payload: Partial<TParamsPagination> & { seen: "all" | null };
                type: string;
            },
        ) => {
            state.optionsPaginationNotifications = {
                ...state.optionsPaginationNotifications,
                ...payload,
            };
        },
        setNotificationRolePermission: (
            state,
            { payload }: { payload: { chosenOptions: TButtonMenuOption[]; notificationType: NotificationType } },
        ) => {
            if (!state.notificationRolesPermissions) return;
            const { chosenOptions, notificationType } = payload;
            const _chosenOptions = new Map(chosenOptions.map((option) => [option.id, option]));

            state.notificationRolesPermissions = state.notificationRolesPermissions.map((nrp) => {
                if (nrp.notificationType !== notificationType) return nrp;
                for (const [role] of Object.entries(nrp.shouldNotify)) {
                    nrp.shouldNotify[role] = _chosenOptions.has(role) ? 1 : 0;
                }
                return nrp;
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getNotifications.fulfilled, (state, action) => {
            // The response body has been seen without the expected list in production
            state.countNotifications = action.payload?.count ?? 0;
            state.notifications = action.payload?.entries ?? [];
        });
        builder.addCase(getNotifications.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getAllNotifications.fulfilled, (state, action) => {
            state.allNotifications = action.payload.entries;
        });
        builder.addCase(getAllNotifications.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(markNotifAsSeen.fulfilled, (state, action) => {
            toast.success(`${t("Notifications.MarkAsSeenSuccess")}`);
            if (typeof action.meta.arg.id === "number") {
                if (state.optionsPaginationNotifications.seen === "all") {
                    state.notifications = state.notifications.filter((notif) => notif.id != action.meta.arg.id);
                } else {
                    state.notifications = state.notifications.filter((notif) => notif.id !== action.meta.arg.id);
                    state.countNotifications = state.countNotifications--;
                }
            } else {
                if (state.optionsPaginationNotifications.seen === "all") {
                    state.notifications = state.notifications.filter(
                        (notif) => !(action.meta.arg.id as number[]).includes(notif.id),
                    );
                    state.allNotifications = state.allNotifications.map((notif) => {
                        (action.meta.arg.id as number[]).includes(notif.id) && (notif.seen = true);
                        return notif;
                    });
                } else {
                    state.notifications = [];
                    state.countNotifications = 0;
                }
            }
        });
        builder.addCase(markNotifAsSeen.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(markAllNotifAsSeen.fulfilled, (state, action) => {
            toast.success(`${t("Notifications.MarkAsSeenSuccess")}`);
            state.allNotifications = state.allNotifications.map((notif) => {
                (action.meta.arg.id as number[]).includes(notif.id) && (notif.seen = true);
                return notif;
            });
            state.notifications = state.notifications.filter(
                (notif) => !(action.meta.arg.id as number[]).includes(notif.id),
            );
        });
        builder.addCase(markAllNotifAsSeen.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getNotificationRoles.pending, (state) => {
            state.fetchStatus.notificationRolesPermissions = "pending";
        });
        builder.addCase(getNotificationRoles.fulfilled, (state, action) => {
            const { data } = action.payload;
            state.notificationRolesPermissions = data;
            state.notificationRolesOptions = formatNotificationRolesOptions(data);
            state.fetchStatus.notificationRolesPermissions = "succeeded";
        });
        builder.addCase(getNotificationRoles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.fetchStatus.notificationRolesPermissions = "failed";
        });
        builder.addCase(updateNotificationRoles.pending, (state) => {
            state.fetchStatus.updateNotificationRoles = "pending";
        });
        builder.addCase(updateNotificationRoles.fulfilled, (state, action) => {
            const { data } = action.payload;
            state.notificationRolesPermissions = data;
            state.notificationRolesOptions = formatNotificationRolesOptions(data);
            state.fetchStatus.updateNotificationRoles = "succeeded";
            toast.success(`${t("Notifications.UpdateRolesSuccess")}`);
        });
        builder.addCase(updateNotificationRoles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.fetchStatus.updateNotificationRoles = "failed";
        });
    },
});

export const { setOptionsPaginationNotifications, setNotificationRolePermission } = notificationSlice.actions;
export default notificationSlice.reducer;
